import type { Rarity } from '../rarity';

/**
 * The banner, its prices and its odds - one versioned, typed table.
 *
 * ## Why every number lives here
 *
 * A price duplicated in a component is a price that will disagree with the
 * server the first time one of them changes, and the disagreement shows up as
 * a charge the player did not expect. The panel reads these constants; the
 * service charges from them; a test compares the two.
 *
 * ## Versioned, because a historical pull must stay explicable
 *
 * `GACHA_ODDS_VERSION` is recorded with every roll. When the table changes, a
 * roll from last month can still be re-evaluated against the rules that
 * actually produced it - which is the whole point of an append-only audit.
 * Change the odds, change the version.
 *
 * ## Integers only
 *
 * The draw is decided in POINTS out of one million, never in floating point.
 * `0.1 + 0.2 !== 0.3`, and an economy that can be argued with is an economy
 * somebody will argue with. Percentages exist in this file only as the label
 * the interface prints.
 */

/**
 * The one banner that accepts pulls.
 *
 * Versioned from the start, which is what made the v2 table possible without a
 * migration that has to guess which pulls belonged to which rules: pity is
 * stored per (account, banner), and every event records the banner that
 * produced it. Gold and Cash on THIS banner deliberately share one counter -
 * they are two ways to pay for the same thing.
 */
export const GACHA_BANNER_ID = 'kenocyber_core_v2';

/**
 * Banners that existed and no longer sell anything.
 *
 * A pull naming one of these is refused, because the odds and the guarantee it
 * was written against are no longer the odds and the guarantee this service
 * applies. Their rows are NOT deleted: the history stays readable and the pity
 * they had accumulated was carried forward by `0023_gacha_banner_v2.sql`.
 */
export const GACHA_RETIRED_BANNER_IDS: readonly string[] = Object.freeze(['kenocyber_core_v1']);

/** Bump when any number below changes. Recorded with every roll. */
export const GACHA_ODDS_VERSION = 'core_v2';

/** The scale the draw is decided on. One million points, all integers. */
export const GACHA_TOTAL_POINTS = 1_000_000;

/**
 * The odds, in points out of {@link GACHA_TOTAL_POINTS}.
 *
 * Ordered from most to least likely, and consumed in this order by the
 * cumulative walk in `draw.ts`, so the boundary of each band is a property of
 * this list rather than of the code that reads it.
 *
 * ## What v2 changed
 *
 * Legendary through Prismatic are each exactly one and a half times what v1
 * gave them - 25 000 → 37 500, 15 000 → 22 500, 6 000 → 9 000, 3 000 → 4 500,
 * 1 000 → 1 500 - and the 25 000 points that costs came out of Common alone
 * (450 000 → 425 000). Uncommon, Rare, Epic and Mythic are untouched.
 *
 * Halving is exact in integers here because every one of the five is even at
 * this scale; a "+50%" that had to round would be a different promise from the
 * one the design made, so the multiplication is asserted rather than assumed.
 *
 * The pool inside each band did not change, so the chance of a Legendary SHIP
 * rose by the same 50% as the band did.
 */
export const GACHA_RARITY_POINTS: Readonly<Record<Rarity, number>> = Object.freeze({
  common: 425_000,
  uncommon: 250_000,
  rare: 140_000,
  epic: 70_000,
  mythic: 40_000,
  legendary: 37_500,
  excellent: 22_500,
  perfect: 9_000,
  anomaly: 4_500,
  prismatic: 1_500,
});

/** The order the bands are laid out in, and the order the interface lists them. */
export const GACHA_RARITY_ORDER: readonly Rarity[] = Object.freeze([
  'common',
  'uncommon',
  'rare',
  'epic',
  'mythic',
  'legendary',
  'excellent',
  'perfect',
  'anomaly',
  'prismatic',
]);

/**
 * How many rolls without a Perfect, Anomaly or Prismatic before one is forced.
 *
 * Forty means the 40th roll is guaranteed when the previous thirty-nine gave
 * none: the counter is how many have PASSED, so it reaches 39 and the next roll
 * is forced. It was sixty in v1.
 *
 * `0023_gacha_banner_v2.sql` carries every existing counter across at
 * `least(counter, 39)`, so nobody loses progress and nobody who had already
 * passed the new threshold is left waiting - their next roll is the guaranteed
 * one. That literal 39 is `GACHA_PITY_THRESHOLD - 1`, and a test compares the
 * two rather than trusting that they were changed together.
 */
export const GACHA_PITY_THRESHOLD = 40;

/**
 * What a forced roll may produce, each with exactly one third of the chance.
 *
 * Equal thirds rather than the normal weights, and the interface says so in
 * numbers: a guarantee that quietly favoured the cheapest of the three would
 * be a worse promise than no guarantee.
 */
export const GACHA_PITY_RARITIES: readonly Rarity[] = Object.freeze([
  'perfect',
  'anomaly',
  'prismatic',
]);

/** True when a rarity resets the counter. The same list, as a question. */
export function resetsPity(rarity: Rarity): boolean {
  return GACHA_PITY_RARITIES.includes(rarity);
}

/** How many rolls one request may buy. Nothing else is a valid quantity. */
export const GACHA_PULL_SIZES = [1, 10] as const;
export type GachaPullSize = (typeof GACHA_PULL_SIZES)[number];

/**
 * What a pull costs, per currency and per size, as exact `bigint`.
 *
 * The ten-pull is priced at ten singles minus a tenth - 1 800 000 rather than
 * 2 000 000 - and that discount is the ONLY one. VIP does not reduce it:
 * VIP already multiplies what a player earns, and stacking a second advantage
 * onto the thing that hands out Prismatic pets is how a subscription stops
 * being optional.
 */
export const GACHA_PRICES: Readonly<
  Record<'gold' | 'cash', Readonly<Record<GachaPullSize, bigint>>>
> = Object.freeze({
  gold: Object.freeze({ 1: 200_000n, 10: 1_800_000n }),
  cash: Object.freeze({ 1: 10n, 10: 90n }),
});

export function gachaPrice(currency: 'gold' | 'cash', size: GachaPullSize): bigint {
  return GACHA_PRICES[currency][size];
}

/**
 * The odds as a label, for a screen that must show real numbers.
 *
 * Derived from the points rather than typed twice, so "2,25%" can never drift
 * from 22 500. Formatted with a comma, which is how this game writes decimals.
 *
 * Always two decimals, and the same two for every band. v2 needs them - 3,75%
 * and 0,15% do not survive one - and a column where some rows carry a decimal
 * and others do not is a column a player has to read twice. The arithmetic is
 * integer throughout: 1% is 10 000 points, so a hundredth of a percent is 100,
 * and `toFixed` never gets near a number that came out of a division.
 */
export function rarityChanceLabel(rarity: Rarity): string {
  const points = GACHA_RARITY_POINTS[rarity];
  if (points % 100 !== 0) {
    /*
     * A band finer than a hundredth of a percent would be silently rounded on
     * the one screen that exists to state the odds exactly. Loud instead: the
     * label needs another decimal, and that is a decision, not a rounding.
     */
    throw new RangeError(
      `${rarity} is ${points} points, which is finer than 0,01% - the label would round it`,
    );
  }
  const hundredths = points / 100;
  return `${Math.floor(hundredths / 100)},${String(hundredths % 100).padStart(2, '0')}%`;
}

/** Total points across the ten bands. Must be exactly the scale. */
export function totalRarityPoints(): number {
  return GACHA_RARITY_ORDER.reduce((sum, rarity) => sum + GACHA_RARITY_POINTS[rarity], 0);
}
